import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import PageTitle from "./PageTitle";
import LayerdIcon from "./LayerdIcon";
import FormHelpTooltip from "./FormHelpTooltip";
import colors from "../../utils/colors";

function isMuiIconComponent(Icon) {
  if (!Icon) return false;
  return Boolean(
    Icon.muiName ||
      Icon.type?.muiName ||
      Icon.displayName?.includes?.("SvgIcon") ||
      Icon.type?.displayName?.includes?.("SvgIcon")
  );
}

const muiHeaderIconSx = { color: colors.primary[500], fontSize: 22, flexShrink: 0 };

/** Subtitle below the page title — shared across v2 pages (SSL/TLS, settings, services, etc.). */
export const pageDescriptionSx = {
  fontSize: 13,
  lineHeight: "20px",
  color: `${colors.neutral[1000]} !important`,
  maxWidth: 680,
  mt: 1.5,
  "& strong": {
    color: colors.neutral[1000],
    fontWeight: 600,
  },
};

/**
 * Standard v2 page heading: icon + title row, optional description, optional actions.
 * Pass a Lucide component, MUI SvgIcon, a pre-rendered node, or <LayerdIcon />.
 */
export default function PageHeader({
  icon: Icon,
  title,
  description,
  helpTooltip,
  actions,
  sx,
  titleSx,
  descriptionSx,
  iconSize = "lg",
  iconColor = "primary",
}) {
  const renderIcon = () => {
    if (!Icon) return null;
    if (React.isValidElement(Icon)) return Icon;

    // MUI icons are often React.memo(forwardRef(SvgIcon)) — an object, not a function.
    if (isMuiIconComponent(Icon) || (Icon.$$typeof && isMuiIconComponent(Icon.type))) {
      const MuiIcon = Icon;
      return <MuiIcon sx={muiHeaderIconSx} />;
    }

    if (typeof Icon === "function") {
      return <LayerdIcon icon={Icon} size={iconSize} color={iconColor} />;
    }

    // memo/forwardRef component type (e.g. @mui/icons-material default export)
    if (Icon.$$typeof && typeof Icon.type === "function") {
      return <Icon sx={muiHeaderIconSx} />;
    }

    return null;
  };

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      sx={{ flexWrap: "wrap", gap: 2, pt: 0.5, pb: 3, ...sx }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: "nowrap" }}>
          {renderIcon()}
          <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap", minWidth: 0 }}>
            <PageTitle component="h1" sx={{ mb: 0, ...titleSx }}>
              {title}
            </PageTitle>
            <FormHelpTooltip tooltip={helpTooltip} />
          </Stack>
        </Stack>
        {description ? (
          <Typography component="div" sx={{ ...pageDescriptionSx, ...descriptionSx }}>
            {description}
          </Typography>
        ) : null}
      </Box>
      {actions ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {actions}
        </Stack>
      ) : null}
    </Stack>
  );
}
